<template>
  <div class="referral-program-cashback">
    <HeadSection
      :breadcrumbs="breadcrumbs"
      :permissionOfSection="permissionOfSection"
    >
       <template #actions>
        <a-col :span="14" class="d-flex justify-content-end">
          <a-button
            type="default"
            size="large"
            class="mr-3"
            :disabled="loading || !haveData"
            @click="toggleShowExportModal()"
          >
            <a-icon type="upload" />
            Export
          </a-button>
        </a-col>
       </template>
    </HeadSection>
    <MainContainer :loading="userLoading" :permissionOfSection="permissionOfSection">
      <div class="d-flex mb-3" style="gap: 20px;">
        <div class="d-flex flex-column">
          <span class="font-weight-bold">Date</span>
          <div class="d-flex align-items-center" style="gap: 5px;">
            <a-date-picker
              v-model="filters.date_start"
              format="YYYY-MM-DD HH:mm"
              :show-time="{
                defaultValue: $moment('00:00:00', 'HH:mm'),
                format: 'HH:mm'
              }"
              type="date"
              size="large"
              placeholder="Start date"
              :disabled="loading || !hasListData"
              @change="() => {resetPage(); getCashbackData()}"
            />
            <span>-</span>
            <a-date-picker
              v-model="filters.date_end"
              format="YYYY-MM-DD HH:mm"
              :show-time="{
                defaultValue: $moment('00:00:00', 'HH:mm'),
                format: 'HH:mm'
              }"
              type="date"
              size="large"
              placeholder="End date"
              :disabled="loading || !hasListData"
              @change="() => {resetPage(); getCashbackData()}"
            />
          </div>
        </div>
        <div class="d-flex flex-column">
          <span class="font-weight-bold">Amount</span>
          <div class="d-flex align-items-center" style="gap: 5px;">
            <a-input-number
              v-model="filters.amount_min"
              type="number"
              size="large"
              placeholder="Min"
              style="min-width: 120px;"
              :disabled="!hasListData"
              :loading="loading"
              @change="changeAmountFilters('amountMinTimeout')"
            />
            <span>-</span>
            <a-input-number
              v-model="filters.amount_max"
              type="number"
              size="large"
              placeholder="Max"
              style="min-width: 120px;"
              :disabled="!hasListData"
              :loading="loading"
              @change="changeAmountFilters('amountMaxTimeout')"
            />
          </div>
        </div>
        <UniversalSelect
          v-model="filters.role"
          style="width: 180px;"
          :options="availableRoles"
          placeholder="Role"
          title="Role"
          :disabled="loading || getAvailableRolesLoading || !hasListData"
          @change="() => {resetPage(); getCashbackData()}"
        />
        <UniversalSelect
          v-model="filters.status"
          style="width: 180px;"
          :options="statusOptions"
          placeholder="Status"
          title="Status"
          :disabled="loading || !hasListData"
          @change="() => {resetPage(); getCashbackData()}"
        />
        <div class="d-flex align-items-end" style="height: 62px;">
          <SearchField
            :value="filters.login"
            :loading="loading"
            :disabled="loading || !hasListData"
            @search="changeSearchValue"
          />
        </div>
        <div class="d-flex align-items-end">
          <a-button
            v-if="isFiltered"
            size="large"
            :disabled="loading"
            class="w-fit"
            @click="() => handleReset()"
          >
            Reset filters
          </a-button>
        </div>
      </div>
      <a-table
        class="cashback-table"
        :dataSource="data"
        :pagination="meta"
        :columns="columnsComputed"
        :row-key="(record) => record.id"
        :customRow="customRow"
        :loading="loading"
        @change="changePagination"
      >
        <template slot="created_at" slot-scope="created_at">
          <span>{{ formatDate(created_at) }}</span>
        </template>
        <template slot="account" slot-scope="account">
          <CopyValue
            :value="account.login"
            message="Login ID copied to clipboard"
          />
        </template>
        <template slot="role" slot-scope="role">
          <a-badge
            v-if="role && role?.name"
            :count="role.name"
            style="cursor: default; transform: scale(1.2);"
            :number-style="{
              backgroundColor: role.name === 'Referral' ? '#52c41a' : '#108ee9',
              boxShadow: '0 4px 8px 0 #d9d9d9',
            }"
          />
          <span v-else>—</span>
        </template>
        <template slot="status" slot-scope="status, record">
          <div class="d-flex align-items-center">
            <a-badge
              :count="getStatusName(status)"
              style="cursor: default; transform: scale(1.2);"
              :number-style="{
                backgroundColor: getBackgroundStatus(status),
                boxShadow: '0 4px 8px 0 #d9d9d9',
              }"
            />
            <div
              v-if="status === 'created'"
              class="d-block px-2 ml-3 text-primary cursor-pointer"
              @click.stop="toggleShowValidateModal(true, record)"
            >
              Validate
            </div>
          </div>
        </template>
      </a-table>
    </MainContainer>
    <ValidateCashbackModal
      v-if="showValidateModal"
      :show="showValidateModal"
      :validateItem="selectedValidateItem"
      @updateTable="getCashbackData"
      @close="toggleShowValidateModal"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import UniversalSelect from '@/components/custom/filters/universalSelect.vue'
import SearchField from '@/components/custom/form/SearchField.vue'
import CopyValue from '@/components/custom/form/CopyValue.vue'
import ValidateCashbackModal from '@/views/apps/referral-program/referral-programs/components/ValidateCashbackModal.vue'
import { getTableMeta } from '@/composables/useGetTableMeta.js'
import { getProgramParams } from '@/views/apps/referral-program/referral-programs/composables/useGetProgramParams.js'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'
import { setQueryParams, setFilters } from '@/composables/useSetQueryParams.js'
import { capitalizeFirstLetter } from '@/composables/useCapitalizeFirstLetter.js'
import { getCashbackStatusColors } from '@/views/apps/referral-program/referral-programs/composables/useGetCashbackStatusColors'
import { getCashbackStatuses } from '@/views/apps/referral-program/referral-programs/composables/useGetCashbackStatuses.js'
import { getFilledFilters } from '@/composables/useGetFilledFilters.js'
import { getReferralRoles } from '@/views/apps/referral-program/composables/useGetReferralRoles.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  components: {
    HeadSection,
    MainContainer,
    UniversalSelect,
    SearchField,
    CopyValue,
    ValidateCashbackModal,
  },
  data: () => ({
    permissionOfSection: 'referral_program',
    program: null,
    programId: null,
    data: [],
    meta: {
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '40'],
    },
    filters: {
      page: undefined,
      per_page: undefined,
      date_start: undefined,
      date_end: undefined,
      role: undefined,
      status: undefined,
      amount_min: undefined,
      amount_max: undefined,
      login: undefined,
      sort_field: undefined,
      sort_dir: undefined,
    },
    columns: [
      {
        title: 'Date',
        dataIndex: 'created_at',
        width: '10%',
        ellipsis: true,
        scopedSlots: { customRender: 'created_at' },
      },
      {
        title: 'Login',
        dataIndex: 'account',
        width: '15%',
        scopedSlots: { customRender: 'account' },
      },
      {
        title: 'Role',
        dataIndex: 'role',
        width: '15%',
        scopedSlots: { customRender: 'role' },
      },
      {
        title: 'Amount',
        dataIndex: 'amount',
        width: '15%',
        scopedSlots: { customRender: 'amount' },
        sorter: true,
        sortOrder: undefined,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: '10%',
        scopedSlots: { customRender: 'status' },
      },
    ],
    amountMinTimeout: null,
    amountMaxTimeout: null,
    statusOptions: getCashbackStatuses(),
    selectedValidateItem: null,
    showValidateModal: false,
    availableRoles: [],
    getAvailableRolesLoading: false,
    loading: false,
  }),
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    innerProgramName() {
      return this.program?.name || ''
    },
    breadcrumbs() {
      return [
        { name: 'Referral programs', link: '/referral/programs' },
        { name: this.innerProgramName, link: `/referral/programs/${this.program?.id}` },
        { name: 'Cashback Withdrawal', link: null },
      ]
    },
    haveData() {
      return !!this.data?.length
    },
    isHasListItems() {
      return !!this.data?.length
    },
    isFiltered() {
      return Object.values(this.filters).some(v => v !== undefined && v !== null && v !== '')
    },
    hasListData() {
      return this.isHasListItems || this.isFiltered
    },
    columnsComputed() {
      return this.columns.map((col) => {
        if (col.dataIndex === 'amount') {
          return {
            ...col,
            sortOrder:
              this.filters.sort_field === col.dataIndex
                ? this.filters.sort_dir === 'asc'
                  ? 'ascend'
                  : 'descend'
                : false,
          }
        }
        return col
      })
    },
  },
  methods: {
    customRow(row) {
      return {
        on: {
          click: () => {
            if (!row?.member?.id) {
              return
            }
            this.$router.push(`/referral/programs/${this.programId}/members/${row.role.system_name}/${row.member.id}`)
          },
        },
      }
    },
    formatDate(date) {
      return this.$moment(date).tz(this.$moment.tz.guess()).format('DD.MM.YYYY')
    },
    getStatusName(status) {
      return capitalizeFirstLetter(status)
    },
    getBackgroundStatus(status) {
      return getCashbackStatusColors(status)
    },
    toggleShowExportModal() {
      this.$store.commit('modal/SET_SHOW_MODAL', {
        title: 'Export cshback withdrawal',
        filter: {
          type: 'cashback',
          filter: {
            ...this.filters,
          },
        },
      })
    },
    resetPage() {
      this.filters.page = 1
    },
    toggleShowValidateModal(status = false, item = null) {
      this.selectedValidateItem = item
      this.showValidateModal = status
    },
    changeAmountFilters(target) {
      clearTimeout(this[target])

      this[target] = setTimeout(() => {
        this.resetPage()
        this.getCashbackData()
        clearTimeout(this[target])
      }, 1000)
    },
    changeSearchValue(value) {
      this.resetPage()
      this.filters.login = value
      this.getCashbackData()
    },
    changePagination(pagination, _, sorter) {
      this.filters.page = pagination.current
      this.filters.per_page = pagination.pageSize

      if (sorter && sorter?.order) {
        this.filters.sort_field = sorter.field
        this.filters.sort_dir = sorter.order === 'ascend' ? 'asc' : 'desc'
      } else {
        this.filters.sort_field = undefined
        this.filters.sort_dir = undefined
      }

      this.getCashbackData()
    },
    getFilters() {
      setQueryParams(this.filters, this, '', true)
      if (!this.isFiltered) {
        return {}
      }

      return getFilledFilters({ filters: this.filters, dateFields: ['date_start', 'date_end'] })
    },
    async getCashbackData() {
      try {
        this.loading = true

        const filters = this.getFilters()

        const url = `${referralApiUrl}/referral-programs/${this.programId}/cashback`
        const response = await apiClient.get(url, { params: filters })
        if (response?.data?.data) {
          this.data = response.data.data
          const newMeta = getTableMeta(response?.data?.meta)
          Object.assign(this.meta, newMeta)
          return
        }
        getErrorMessage(response?.data)
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loading = false
      }
    },
    async getProgramParams() {
      const data = await getProgramParams(this.programId)
      if (data) {
        this.program = data
        return
      }
      this.$router.push('/referral/programs')
    },
    async getAvailableRoles() {
      try {
        this.getAvailableRolesLoading = true
        const roles = await getReferralRoles() || []
        this.availableRoles = roles.map(role => {
          return {
            name: `${capitalizeFirstLetter(role.name)}`,
            value: role.id,
          }
        })
      } finally {
        this.getAvailableRolesLoading = false
      }
    },
    handleReset() {
      Object.keys(this.filters).forEach(key => {
        this.filters[key] = undefined
      })
      this.getCashbackData()
    },
  },
  async mounted() {
    this.programId = this.$route.params.id
    if (Object.keys(this.$route.query)?.length) {
      setFilters(this.$route.query, this, '', true)
    }
    const requests = [this.getAvailableRoles(), this.getProgramParams(), this.getCashbackData()]

    await Promise.all(requests)
  },
}
</script>

<style lang="scss">
.referral-program-cashback {
  .ant-table-row {
    cursor: pointer;
  }
}
</style>
